'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link } from '@/i18n/routing';

interface NewsletterSignupProps {
  title: string;
  text: string;
  discountCode: string;
  source?: string;
  locale?: string;
}

const copy = {
  nl: {
    eyebrow: 'Nieuwsbrief',
    placeholder: 'jij@email.nl',
    loading: 'Moment...',
    button: 'Ontvang korting',
    helper: 'Schrijf je in en ontvang direct je kortingscode.',
    consent: 'Ja, ik wil aanbiedingen en printtips per e-mail ontvangen. Afmelden kan altijd.',
    code: 'Je code is',
    error: 'Inschrijven mislukt',
  },
  de: {
    eyebrow: 'Newsletter',
    placeholder: 'du@email.de',
    loading: 'Moment...',
    button: 'Rabatt erhalten',
    helper: 'Melde dich an und erhalte deinen Rabattcode direkt.',
    consent: 'Ja, ich möchte Angebote und Drucktipps per E-Mail erhalten. Abmeldung jederzeit möglich.',
    code: 'Dein Code ist',
    error: 'Anmeldung fehlgeschlagen',
  },
  en: {
    eyebrow: 'Newsletter',
    placeholder: 'you@email.com',
    loading: 'One moment...',
    button: 'Get discount',
    helper: 'Subscribe and receive your discount code immediately.',
    consent: 'Yes, I want to receive offers and print tips by email. I can unsubscribe at any time.',
    code: 'Your code is',
    error: 'Subscription failed',
  },
  fr: {
    eyebrow: 'Newsletter',
    placeholder: 'vous@email.fr',
    loading: 'Un instant...',
    button: 'Recevoir la remise',
    helper: 'Inscrivez-vous et recevez votre code de réduction.',
    consent: 'Oui, je souhaite recevoir des offres et conseils par e-mail. Desinscription possible a tout moment.',
    code: 'Votre code est',
    error: "L'inscription a echoue",
  },
};

export function NewsletterSignup({ title, text, discountCode, source = 'homepage', locale = 'nl' }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);
  const t = copy[locale as keyof typeof copy] || copy.nl;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source, locale, consent }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t.error);
      window.printenTrack?.('newsletter_signup', {
        eventId: String(data.eventId || `newsletter_${Date.now()}`),
        email,
        source,
        value: 0,
        currency: 'EUR',
      });
      setStatus('done');
      setMessage(`${t.code} ${data.discountCode || discountCode}.`);
      setEmail('');
      setConsent(false);
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : t.error);
    }
  }

  return (
    <div className="rounded-[2rem] bg-blue-600 p-6 text-white shadow-xl md:p-8">
      <div className="grid gap-5 md:grid-cols-[1fr_0.9fr] md:items-center">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-white">{t.eyebrow}</p>
          <h2 className="mt-2 text-3xl font-black">{title}</h2>
          <p className="mt-3 text-blue-50">{text}</p>
        </div>
        <form onSubmit={submit} className="rounded-2xl bg-white p-3 shadow-lg">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t.placeholder}
              className="text-slate-950"
            />
            <Button type="submit" disabled={status === 'loading'} className="bg-blue-600 hover:bg-blue-700">
              {status === 'loading' ? t.loading : t.button}
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {status === 'done' ? message : t.helper}
          </p>
          {status !== 'done' && (
            <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs leading-5 text-slate-600">
              <input
                type="checkbox"
                required
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-blue-600"
              />
              <span>{t.consent} <Link href="/privacy" className="underline">Privacy</Link></span>
            </label>
          )}
          {message && status !== 'done' && (
            <p className={`mt-2 text-sm font-semibold ${status === 'error' ? 'text-red-600' : 'text-emerald-700'}`}>
              {message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
